import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faUsers, 
    faClock, 
    faCheckCircle,
    faTimesCircle,
    faSearch,
    faExclamationTriangle,
    faSpinner,
    faRobot,
    faRefresh,
    faExternalLink,
    faEnvelope,
    faCheck,
    faTimes as faTimesIcon,
    faDatabase,
    faNewspaper,
    faChartLine,
    faPercent,
    faChevronDown,
    faChevronUp,
    faBullhorn
} from '@fortawesome/free-solid-svg-icons';
import axios from 'axios';
import config from '../../config';
import EditSponsorModal from '../../components/EditSponsorModal';
import CompanyLogo from '../../components/CompanyLogo';
import NewsletterManager from '../../components/NewsletterManager';
import { normalizeTags } from '../../utils/normalizeTags';
import '../../css/pages/authReq/Admin.css';

interface Sponsor {
    _id: string;
    sponsorName: string;
    sponsorLink: string;
    rootDomain: string;
    tags: string[];
    newsletterSponsored: string; // Single newsletter for this record
    subscriberCount: number; // Audience size for this newsletter
    dateSponsored?: string | Date; // Date this newsletter placement occurred
    sponsorEmail?: string;
    businessContact?: string;
    contactMethod: 'email' | 'none';
    // Detailed contact info from Gemini
    contactPersonName?: string;
    contactPersonTitle?: string;
    contactType?: 'named_person' | 'business_email' | 'generic_email' | 'not_found';
    confidence?: number;
    dateAdded: string;
    status: 'pending' | 'approved' | 'previouslyApproved' | 'hunterFailed' | 'needs_review' | 'rejected' | 'needs_enrichment';
    emailVerified?: boolean;
    hunterConfidence?: number | null;
    vetting_confidence?: 'high' | 'medium' | 'low';
    vetting_reason?: string;
    approval_reason?: string;
    rejection_reason?: string;
    // New structure fields
    newslettersSponsored?: Array<{
        newsletterName: string;
        estimatedAudience: number;
        contentTags: string[];
        dateSponsored: string | Date;
        emailAddress?: string;
    }>;
    contentTags?: string[]; // Tags for this specific newsletter placement
    totalPlacements?: number; // Total number of newsletter placements
    avgAudienceSize?: number; // Average audience size across all newsletters
    mostRecentNewsletterDate?: string | Date; // Most recent newsletter date
    // User tracking
    isViewed?: boolean;
    isApplied?: boolean;
    dateViewed?: string | Date;
    dateApplied?: string | Date;
}

interface DashboardStats {
    totalSponsors: number;
    pendingReview: number;
    addedThisWeek: number;
    lastScraperRun: string;
    weeklyData?: Array<{
        week: string;
        count: number;
    }>;
}

interface VettingAgentStatus {
    lastRun: {
        runAt: string;
        processed: number;
        approved: number;
        rejected: number;
        needsReview: number;
        errors: number;
    } | null;
    pendingCount: number;
    nextScheduledRun: string;
    agentConfigured: boolean;
}

interface MarketingDraft {
    _id: string;
    subreddit: string;
    threadTitle: string;
    threadUrl: string;
    threadExcerpt: string;
    threadAuthor: string;
    draftReply: string;
    editedReply: string | null;
    status: 'pending_review' | 'posted' | 'dismissed';
    dateFound: string;
    postedAt: string | null;
}

interface SocialDraft {
    _id: string;
    platform: 'reddit_post' | 'x_post';
    subreddit: string;
    title: string;
    body: string;
    editedBody: string | null;
    charLimit: number | null;
    status: 'pending_review' | 'posted' | 'dismissed';
    dateGenerated: string;
    postedAt: string | null;
}

interface AdminOverview {
    users: { total: number; paying: number; trialing: number; newThisWeek: number; mrr: number };
    sponsors: { live: number; newThisWeek: number; pipeline: Record<string, number> };
    vetting: { runAt: string; processed: number; approved: number; rejected: number; needsReview: number } | null;
    hunter: {
        creditsRemaining: number;
        creditsAvailable: number | null;
        resetDate: string | null;
        planName: string | null;
        live: boolean;
    } | null;
}

interface NewsletterRequestItem {
    _id: string;
    newsletterName: string;
    newsletterUrl?: string;
    niche?: string;
    notes?: string;
    requesterEmail?: string;
    status: 'pending' | 'added' | 'declined';
    dateRequested: string;
}

interface FeedbackItem {
    _id: string;
    email: string;
    name?: string;
    message: string;
    source: 'general' | 'cancellation';
    status: 'new' | 'reviewed';
    adminNotes?: string;
    createdAt: string;
}

interface AdminUser {
    _id: string;
    email: string;
    name: string;
    signedUpAt: string;
    accessLevel: 'admin' | 'paying' | 'trialing' | 'lifetime' | 'free';
    billingStatus: string | null;
    trialEnd: string | null;
    monthlyCharge: number;
    cancelAtPeriodEnd: boolean;
    onboardingStage: string;
    emailUnsubscribed: boolean;
    newsletterName: string;
    newsletterAudience: number;
    sponsorsViewed: number;
    sponsorsApplied: number;
    sponsorsContacted: number;
    sponsorsResponded: number;
    sponsorDealsClosed: number;
    sponsorRevenue: number;
    sortGroup: number;
}

// Matches the sortGroup the backend assigns in GET /admin/users (0/1/2) —
// users arrive pre-sorted into these groups, most sponsors-applied first
// within each group.
const USER_GROUP_LABELS: Record<number, string> = {
    0: 'Active Subscription',
    1: 'Trialing',
    2: 'Canceled',
    3: 'Other Users',
};

// Real sponsor records have 7 raw statuses (pending, approved,
// previouslyApproved, hunterFailed, needs_enrichment, rejected,
// needs_review), but day-to-day there are really only three questions:
// does this need my attention, is it live, or did I turn it down. Grouped
// here instead of one filter button per raw status. Static — lives outside
// the component so fetchData's useCallback doesn't need it as a dependency.
const SPONSOR_FILTERS: { key: string; label: string; statuses: string[] }[] = [
    { key: 'needs_attention', label: 'Needs Attention', statuses: ['pending', 'needs_review', 'needs_enrichment', 'hunterFailed'] },
    { key: 'approved', label: 'Approved', statuses: ['approved', 'previouslyApproved'] },
    { key: 'rejected', label: 'Rejected', statuses: ['rejected'] },
    { key: 'all', label: 'All', statuses: [] },
];

const Admin = () => {
    // Tab state
    const [activeTab, setActiveTab] = useState<'overview' | 'users' | 'sponsors' | 'analytics' | 'social'>('overview');

    // Overview + Users tab state
    const [overview, setOverview] = useState<AdminOverview | null>(null);
    const [overviewLoading, setOverviewLoading] = useState(false);
    // Sunday newsletter-post reminder — dismissal is remembered per calendar
    // day (not permanently), so it comes back next Sunday instead of being
    // silenced forever after the first click.
    const [sundayReminderDismissed, setSundayReminderDismissed] = useState(
        () => localStorage.getItem('sundayReminderDismissedDate') === new Date().toDateString()
    );
    const [newsletterRequests, setNewsletterRequests] = useState<NewsletterRequestItem[]>([]);
    const [newsletterRequestsLoading, setNewsletterRequestsLoading] = useState(false);
    const [feedbackEntries, setFeedbackEntries] = useState<FeedbackItem[]>([]);
    const [feedbackLoading, setFeedbackLoading] = useState(false);
    const [adminUsers, setAdminUsers] = useState<AdminUser[]>([]);
    const [adminUsersLoading, setAdminUsersLoading] = useState(false);
    const [userSearch, setUserSearch] = useState('');
    
    // State management
    const [stats, setStats] = useState<DashboardStats | null>(null);
    const [sponsors, setSponsors] = useState<Sponsor[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    
    // Table state
    const [selectedSponsors, setSelectedSponsors] = useState<string[]>([]);
    const [expandedSponsors, setExpandedSponsors] = useState<Set<string>>(new Set());
    const [searchTerm, setSearchTerm] = useState('');
    const [statusFilter, setStatusFilter] = useState('needs_attention');
    const [currentPage, setCurrentPage] = useState(1);
    // Maintenance/backfill tools (scraper, vetting agent, legacy migration,
    // Hunter backfill, exports) — occasional-use, not daily-glance stuff,
    // so tucked behind one toggle instead of always-visible cards.
    const [toolsExpanded, setToolsExpanded] = useState(false);
    const [isRunningScraper, setIsRunningScraper] = useState(false);
    const [isPerformingBulkAction, setIsPerformingBulkAction] = useState(false);
    const [isPerformingIndividualAction, setIsPerformingIndividualAction] = useState(false);
    const [isConsolidating, setIsConsolidating] = useState(false);
    const [consolidationResults, setConsolidationResults] = useState<any>(null);
    const [showAllSponsors, setShowAllSponsors] = useState(false);
    
    // Test email state
    const [testEmail, setTestEmail] = useState('');
    const [isSendingTestEmail, setIsSendingTestEmail] = useState(false);
    const [testEmailResult, setTestEmailResult] = useState<string | null>(null);

    // Onboarding email test state
    const [onboardingTestEmail, setOnboardingTestEmail] = useState('');
    const [onboardingRecipientEmail, setOnboardingRecipientEmail] = useState('');
    const [sendAllOnboardingVariants, setSendAllOnboardingVariants] = useState(true);
    const [isSendingOnboardingSequence, setIsSendingOnboardingSequence] = useState(false);
    const [onboardingTestResult, setOnboardingTestResult] = useState<{
        type: 'success' | 'error';
        message: string;
        emailsSent?: string[];
    } | null>(null);
    
    // Legacy sponsor migration state
    const [isMigratingLegacySponsors, setIsMigratingLegacySponsors] = useState(false);
    const [legacyMigrationResults, setLegacyMigrationResults] = useState<{
        matchedCount: number;
        modifiedCount: number;
    } | null>(null);

    // Hunter backfill state
    const [isRunningHunterBackfill, setIsRunningHunterBackfill] = useState(false);
    // Per-row "Run Hunter" state — tracks which sponsor's button is mid-request
    const [runningHunterSponsorId, setRunningHunterSponsorId] = useState<string | null>(null);
    const [isExportingLegacyEmails, setIsExportingLegacyEmails] = useState(false);
    const [isIngestingHunterResults, setIsIngestingHunterResults] = useState(false);

    // Newsletter data fix migration state
    const [isFixingNewsletterData, setIsFixingNewsletterData] = useState(false);
    const [newsletterFixResults, setNewsletterFixResults] = useState<any>(null);
    
    // Gemini test state
    const [isTestingGemini, setIsTestingGemini] = useState(false);
    const [geminiTestResults, setGeminiTestResults] = useState<any>(null);
    const [isListingGeminiModels, setIsListingGeminiModels] = useState(false);
    const [geminiModelsList, setGeminiModelsList] = useState<any>(null);
    
    // Affiliates state
    const [affiliates, setAffiliates] = useState<any[]>([]);
    const [affiliatesLoading, setAffiliatesLoading] = useState(false);
    const affiliatesPage = 1;
    const [affiliatesSearchTerm, setAffiliatesSearchTerm] = useState('');
    const [affiliatesStatusFilter, setAffiliatesStatusFilter] = useState('all');
    
    // Edit modal state
    const [editingSponsor, setEditingSponsor] = useState<Sponsor | null>(null);
    
    // User analytics state
    const [userAnalytics, setUserAnalytics] = useState<any>(null);
    const [loadingAnalytics, setLoadingAnalytics] = useState(false);

    // Vetting agent state
    const [vettingAgentStatus, setVettingAgentStatus] = useState<VettingAgentStatus | null>(null);
    const [isRunningVettingAgent, setIsRunningVettingAgent] = useState(false);
    const [vettingAgentMessage, setVettingAgentMessage] = useState<string | null>(null);

    // Hunter approval toast state
    const [hunterToast, setHunterToast] = useState<{ type: 'success' | 'warning'; message: string } | null>(null);

    // Reddit marketing draft queue state
    const [marketingDrafts, setMarketingDrafts] = useState<MarketingDraft[]>([]);
    const [marketingDraftsLoading, setMarketingDraftsLoading] = useState(false);
    const [marketingDraftStatusFilter, setMarketingDraftStatusFilter] = useState<'pending_review' | 'posted' | 'dismissed' | 'all'>('pending_review');
    const [editedDraftText, setEditedDraftText] = useState<Record<string, string>>({});
    const [copiedDraftId, setCopiedDraftId] = useState<string | null>(null);
    const [newThreadUrl, setNewThreadUrl] = useState('');
    const [newThreadSubreddit, setNewThreadSubreddit] = useState('');
    const [newThreadTitle, setNewThreadTitle] = useState('');
    const [newThreadBody, setNewThreadBody] = useState('');
    const [isDraftingReply, setIsDraftingReply] = useState(false);
    const [draftError, setDraftError] = useState<string | null>(null);

    // Community sub-tab: Replies / Reddit Posts / X Posts
    // 'replies' (Reddit-thread-reply drafting) dropped — Social tab now
    // only carries the original weekly self-post generator.
    // Mobile-only: which of the 3 Social columns (Reddit / X / Newsletters)
    // is visible. Desktop ignores this and shows all 3 side by side.
    const [communitySubTab, setCommunitySubTab] = useState<'reddit_post' | 'x_post' | 'newsletters'>('newsletters');

    // Weekly original-post draft queues — separate arrays so Reddit and X
    // can render side by side at the same time (used to be one shared
    // array for whichever platform was last selected, which only worked
    // when just one was ever visible at once).
    const [redditDrafts, setRedditDrafts] = useState<SocialDraft[]>([]);
    const [redditDraftsLoading, setRedditDraftsLoading] = useState(false);
    const [xDrafts, setXDrafts] = useState<SocialDraft[]>([]);
    const [xDraftsLoading, setXDraftsLoading] = useState(false);
    const [socialDraftStatusFilter, setSocialDraftStatusFilter] = useState<'pending_review' | 'posted' | 'dismissed' | 'all'>('pending_review');
    const [editedSocialBody, setEditedSocialBody] = useState<Record<string, string>>({});
    const [copiedSocialField, setCopiedSocialField] = useState<string | null>(null);
    const [isGeneratingSocial, setIsGeneratingSocial] = useState(false);
    const [socialGenerateError, setSocialGenerateError] = useState<string | null>(null);

    // Status filter options
    useEffect(() => {
        if (!hunterToast) return;
        const timer = window.setTimeout(() => setHunterToast(null), 5000);
        return () => window.clearTimeout(timer);
    }, [hunterToast]);

    const fetchOverview = useCallback(async () => {
        try {
            setOverviewLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${config.backendUrl}admin/overview`, {
                headers: { 'x-auth-token': token }
            });
            setOverview(response.data);
        } catch (err) {
            console.error('Error fetching overview:', err);
        } finally {
            setOverviewLoading(false);
        }
    }, []);

    const fetchNewsletterRequests = useCallback(async () => {
        try {
            setNewsletterRequestsLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${config.backendUrl}admin/newsletter-requests`, {
                headers: { 'x-auth-token': token }
            });
            setNewsletterRequests(response.data.requests || []);
        } catch (err) {
            console.error('Error fetching newsletter requests:', err);
        } finally {
            setNewsletterRequestsLoading(false);
        }
    }, []);

    const handleUpdateNewsletterRequestStatus = async (id: string, status: 'added' | 'declined') => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${config.backendUrl}admin/newsletter-requests/${id}`, { status }, {
                headers: { 'x-auth-token': token }
            });
            setNewsletterRequests((prev) => prev.map((r) => (r._id === id ? { ...r, status } : r)));
        } catch (err) {
            console.error('Error updating newsletter request:', err);
        }
    };

    const fetchFeedback = useCallback(async () => {
        try {
            setFeedbackLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${config.backendUrl}admin/feedback`, {
                headers: { 'x-auth-token': token }
            });
            setFeedbackEntries(response.data.entries || []);
        } catch (err) {
            console.error('Error fetching feedback:', err);
        } finally {
            setFeedbackLoading(false);
        }
    }, []);

    const handleMarkFeedbackReviewed = async (id: string) => {
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${config.backendUrl}admin/feedback/${id}`, { status: 'reviewed' }, {
                headers: { 'x-auth-token': token }
            });
            setFeedbackEntries((prev) => prev.map((f) => (f._id === id ? { ...f, status: 'reviewed' } : f)));
        } catch (err) {
            console.error('Error updating feedback:', err);
        }
    };

    const fetchAdminUsers = useCallback(async (search: string = '') => {
        try {
            setAdminUsersLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${config.backendUrl}admin/users`, {
                headers: { 'x-auth-token': token },
                params: search ? { search } : {}
            });
            setAdminUsers(response.data.users || []);
        } catch (err) {
            console.error('Error fetching users:', err);
        } finally {
            setAdminUsersLoading(false);
        }
    }, []);

    const fetchMarketingDrafts = useCallback(async (status: string = 'pending_review') => {
        try {
            setMarketingDraftsLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${config.backendUrl}admin/marketing-drafts`, {
                headers: { 'x-auth-token': token },
                params: { status }
            });
            setMarketingDrafts(response.data.drafts || []);
        } catch (err) {
            console.error('Error fetching marketing drafts:', err);
        } finally {
            setMarketingDraftsLoading(false);
        }
    }, []);

    const handleDraftReply = async () => {
        if (!newThreadUrl.trim() || !newThreadTitle.trim()) {
            setDraftError('Thread URL and title are required.');
            return;
        }
        try {
            setIsDraftingReply(true);
            setDraftError(null);
            const token = localStorage.getItem('token');
            const response = await axios.post(`${config.backendUrl}admin/marketing-drafts/draft`, {
                threadUrl: newThreadUrl.trim(),
                subreddit: newThreadSubreddit.trim(),
                threadTitle: newThreadTitle.trim(),
                threadBody: newThreadBody.trim(),
            }, {
                headers: { 'x-auth-token': token }
            });
            setMarketingDrafts(prev => [response.data.draft, ...prev]);
            setNewThreadUrl('');
            setNewThreadSubreddit('');
            setNewThreadTitle('');
            setNewThreadBody('');
        } catch (err: any) {
            setDraftError(err.response?.data?.error || 'Failed to draft a reply.');
        } finally {
            setIsDraftingReply(false);
        }
    };

    const handleDraftStatusChange = async (draft: MarketingDraft, status: 'posted' | 'dismissed') => {
        setMarketingDrafts(prev => prev.filter(d => d._id !== draft._id));
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${config.backendUrl}admin/marketing-drafts/${draft._id}`, { status }, {
                headers: { 'x-auth-token': token }
            });
        } catch (err) {
            console.error('Failed to update draft status', err);
        }
    };

    const handleSaveDraftEdit = async (draft: MarketingDraft) => {
        const text = editedDraftText[draft._id];
        if (text === undefined) return;
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${config.backendUrl}admin/marketing-drafts/${draft._id}`, { editedReply: text }, {
                headers: { 'x-auth-token': token }
            });
            setMarketingDrafts(prev => prev.map(d => d._id === draft._id ? { ...d, editedReply: text } : d));
        } catch (err) {
            console.error('Failed to save edited draft', err);
        }
    };

    const handleCopyDraft = (draft: MarketingDraft) => {
        const text = editedDraftText[draft._id] ?? draft.editedReply ?? draft.draftReply;
        navigator.clipboard.writeText(text).then(() => {
            setCopiedDraftId(draft._id);
            setTimeout(() => setCopiedDraftId(null), 2000);
        }).catch(() => { /* clipboard optional */ });
    };

    // --- Weekly original-post drafts (Reddit self-posts / X posts) ---

    // Both columns share one status filter (pending/posted/dismissed/all)
    // but keep separate arrays — this picks which pair of setters a given
    // platform's request/update should go through.
    const socialSettersFor = (platform: 'reddit_post' | 'x_post') =>
        platform === 'reddit_post'
            ? { setDrafts: setRedditDrafts, setLoading: setRedditDraftsLoading }
            : { setDrafts: setXDrafts, setLoading: setXDraftsLoading };

    const fetchSocialDrafts = useCallback(async (platform: 'reddit_post' | 'x_post', status: string = 'pending_review') => {
        const { setDrafts, setLoading } = socialSettersFor(platform);
        try {
            setLoading(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${config.backendUrl}admin/social-drafts`, {
                headers: { 'x-auth-token': token },
                params: { platform, status }
            });
            setDrafts(response.data.drafts || []);
        } catch (err) {
            console.error('Error fetching social drafts:', err);
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleGenerateSocial = async (platform: 'reddit_post' | 'x_post') => {
        const { setDrafts } = socialSettersFor(platform);
        try {
            setIsGeneratingSocial(true);
            setSocialGenerateError(null);
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${config.backendUrl}admin/social-drafts/generate/${platform}`,
                {},
                { headers: { 'x-auth-token': token }, timeout: 45000 }
            );
            setDrafts(prev => [response.data.draft, ...prev]);
        } catch (err: any) {
            setSocialGenerateError(err.response?.data?.error || 'Failed to generate a draft.');
        } finally {
            setIsGeneratingSocial(false);
        }
    };

    const handleSocialDraftStatusChange = async (draft: SocialDraft, status: 'posted' | 'dismissed') => {
        const { setDrafts } = socialSettersFor(draft.platform);
        setDrafts(prev => prev.filter(d => d._id !== draft._id));
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${config.backendUrl}admin/social-drafts/${draft._id}`, { status }, {
                headers: { 'x-auth-token': token }
            });
        } catch (err) {
            console.error('Failed to update social draft status', err);
        }
    };

    const handleSaveSocialEdit = async (draft: SocialDraft) => {
        const text = editedSocialBody[draft._id];
        if (text === undefined) return;
        const { setDrafts } = socialSettersFor(draft.platform);
        try {
            const token = localStorage.getItem('token');
            await axios.put(`${config.backendUrl}admin/social-drafts/${draft._id}`, { editedBody: text }, {
                headers: { 'x-auth-token': token }
            });
            setDrafts(prev => prev.map(d => d._id === draft._id ? { ...d, editedBody: text } : d));
        } catch (err) {
            console.error('Failed to save edited social draft', err);
        }
    };

    const handleCopySocialText = (fieldId: string, text: string) => {
        navigator.clipboard.writeText(text).then(() => {
            setCopiedSocialField(fieldId);
            setTimeout(() => setCopiedSocialField(null), 2000);
        }).catch(() => { /* clipboard optional */ });
    };

    useEffect(() => {
        if (activeTab === 'overview') {
            fetchOverview();
            fetchNewsletterRequests();
            fetchFeedback();
        }
        if (activeTab === 'users') fetchAdminUsers();
        if (activeTab === 'social') {
            // Both columns fetch together now — desktop shows them side by
            // side, so both need data regardless of which one mobile's
            // selector currently has active.
            fetchSocialDrafts('reddit_post', socialDraftStatusFilter);
            fetchSocialDrafts('x_post', socialDraftStatusFilter);
        }
    }, [activeTab, fetchOverview, fetchNewsletterRequests, fetchFeedback, fetchAdminUsers, fetchSocialDrafts, socialDraftStatusFilter]);

    const showHunterApprovalToasts = (hunterResults?: Array<{
        success: boolean;
        message: string;
        sponsorName?: string;
        status?: string;
    }>) => {
        if (!hunterResults?.length) return;

        if (hunterResults.length === 1) {
            const result = hunterResults[0];
            const detail = result.sponsorName
                ? `${result.sponsorName}: ${result.message}`
                : result.message;
            setHunterToast({
                type: result.success ? 'success' : 'warning',
                message: detail,
            });
            return;
        }

        const successCount = hunterResults.filter((result) => result.success).length;
        const failureCount = hunterResults.length - successCount;

        if (failureCount === 0) {
            setHunterToast({
                type: 'success',
                message: `${successCount} sponsors approved — verified contacts found`,
            });
        } else if (successCount === 0) {
            setHunterToast({
                type: 'warning',
                message: `${failureCount} sponsors failed Hunter validation — marked for review`,
            });
        } else {
            setHunterToast({
                type: 'warning',
                message: `${successCount} approved, ${failureCount} failed Hunter validation`,
            });
        }
    };

    // Fetch dashboard data
    const fetchData = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            
            const token = localStorage.getItem('token');
            if (!token) {
                setError('Authentication required');
                return;
            }

            const headers = { 'x-auth-token': token };

            // Grouped filters (see SPONSOR_FILTERS) map to a comma-joined
            // list of raw statuses; the server does the actual filtering
            // via $in — no client-side re-filtering here, which is what
            // let the old 'with_contact'/'no_contact' filters silently do
            // nothing (they were never wired server-side OR client-side).
            const activeFilterGroup = SPONSOR_FILTERS.find((f) => f.key === statusFilter);
            const statusParam = activeFilterGroup && activeFilterGroup.statuses.length > 0
                ? activeFilterGroup.statuses.join(',')
                : 'all';

            // Build query parameters
            // limit was hardcoded to 200 — silently truncated real results
            // (419 total sponsors; 243 hunterFailed alone already exceeded
            // that cap, so "Needs Attention" only ever showed the first 200
            // and looked like a suspiciously round, wrong number). 5000 is
            // comfortably above any realistic near-term total; the backend
            // also caps at 5000 defensively regardless of what's requested.
            const queryParams = new URLSearchParams({
                page: currentPage.toString(),
                limit: '5000',
                sortBy: 'dateAdded',
                sortOrder: 'desc',
                search: searchTerm,
                status: statusParam
            });

            // Fetch data
            const [statsRes, sponsorsRes] = await Promise.all([
                axios.get(`${config.backendUrl}admin/stats`, { headers }),
                axios.get(`${config.backendUrl}admin/sponsors/all?${queryParams}`, { headers })
            ]);

            setStats({
                totalSponsors: statsRes.data.totalSponsors,
                pendingReview: statsRes.data.pendingReview,
                addedThisWeek: statsRes.data.scrapedThisWeek,
                lastScraperRun: new Date().toLocaleString()
            });

            setSponsors(sponsorsRes.data.sponsors as Sponsor[]);
        } catch (err: any) {
            setError('Failed to load dashboard data');
        } finally {
            setLoading(false);
        }
    }, [currentPage, searchTerm, statusFilter]);

    const fetchVettingAgentStatus = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;
            const response = await axios.get(`${config.backendUrl}admin/vetting-agent/status`, {
                headers: { 'x-auth-token': token }
            });
            setVettingAgentStatus(response.data);
        } catch {
            // Non-blocking — agent card shows empty state
        }
    }, []);

    // Initial load
    useEffect(() => {
        fetchData();
        fetchVettingAgentStatus();
    }, [fetchData, fetchVettingAgentStatus]);

    const handleRunVettingAgent = async () => {
        try {
            setIsRunningVettingAgent(true);
            setVettingAgentMessage(null);
            setError(null);
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${config.backendUrl}admin/run-vetting-agent`,
                {},
                { headers: { 'x-auth-token': token }, timeout: 60000 }
            );
            if (response.data.started) {
                setVettingAgentMessage(
                    'Run started in background — results appear in Last Run once finished (a few minutes for a full queue)'
                );
            } else {
                setVettingAgentMessage(response.data.message || 'Run triggered');
            }
            await Promise.all([fetchVettingAgentStatus(), fetchData()]);
        } catch (err: any) {
            const msg = err.response?.data?.error || err.message || 'Vetting agent failed';
            setVettingAgentMessage(msg);
        } finally {
            setIsRunningVettingAgent(false);
        }
    };

    const formatVettingNextRun = (iso: string | undefined) => {
        if (!iso) return '9am tomorrow';
        return new Date(iso).toLocaleString('en-US', {
            weekday: 'short',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        });
    };

    // Run scraper
    const handleRunScraper = async () => {
        try {
            setIsRunningScraper(true);
            const token = localStorage.getItem('token');
            const response = await axios.post(`${config.backendUrl}admin/scraper/run`, {}, {
                headers: { 'x-auth-token': token }
            });
            
            if (response.data.success) {
                setTimeout(() => {
                    fetchData();
                }, 2000);
            }
        } catch (err: any) {
            setError('Failed to run scraper');
        } finally {
            setIsRunningScraper(false);
        }
    };

    // Consolidate sponsor data
    const handleConsolidate = async () => {
        try {
            setIsConsolidating(true);
            setError(null);
            const token = localStorage.getItem('token');
            const response = await axios.post(`${config.backendUrl}admin/consolidate-sponsors`, {}, {
                headers: { 'x-auth-token': token }
            });
            
            setConsolidationResults(response.data.results);
            
            // Refresh data after consolidation
            setTimeout(() => {
                fetchData();
            }, 1000);
        } catch (err: any) {
            setError('Failed to consolidate sponsor data');
        } finally {
            setIsConsolidating(false);
        }
    };

    // Test sponsor count
    const handleTestCount = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${config.backendUrl}admin/test-sponsor-count`, {
                headers: { 'x-auth-token': token }
            });

            alert(`Total sponsors in database: ${response.data.totalCount}\nSample: ${JSON.stringify(response.data.sampleSponsors, null, 2)}`);
        } catch (err: any) {
            setError('Failed to test sponsor count');
        }
    };

    // Handle test email
    const handleTestEmail = async () => {
        if (!testEmail.trim()) {
            setTestEmailResult('Please enter an email address');
            return;
        }

        try {
            setIsSendingTestEmail(true);
            setTestEmailResult(null);
            
            const token = localStorage.getItem('token');
            await axios.post(`${config.backendUrl}users/test-email`, {
                email: testEmail.trim()
            }, {
                headers: { 'x-auth-token': token }
            });

            setTestEmailResult('Test email sent successfully!');
            setTestEmail('');
        } catch (err: any) {
            setTestEmailResult(`Failed to send test email: ${err.response?.data?.error || err.message}`);
        } finally {
            setIsSendingTestEmail(false);
        }
    };

    const handleTestOnboardingSequence = async () => {
        if (!onboardingTestEmail.trim()) {
            setOnboardingTestResult({
                type: 'error',
                message: 'Enter a SponsorDB user email to load profile data for the templates.',
            });
            return;
        }

        try {
            setIsSendingOnboardingSequence(true);
            setOnboardingTestResult(null);

            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${config.backendUrl}users/test-onboarding-sequence`,
                {
                    email: onboardingTestEmail.trim(),
                    sendAllVariants: sendAllOnboardingVariants,
                    ...(onboardingRecipientEmail.trim()
                        ? { recipientEmail: onboardingRecipientEmail.trim() }
                        : {}),
                },
                { headers: { 'x-auth-token': token } }
            );

            setOnboardingTestResult({
                type: 'success',
                message: response.data.message,
                emailsSent: response.data.emailsSent,
            });
        } catch (err: any) {
            setOnboardingTestResult({
                type: 'error',
                message: err.response?.data?.error || err.message || 'Failed to send onboarding test emails',
            });
        } finally {
            setIsSendingOnboardingSequence(false);
        }
    };

    // Handle newsletter data fix
    const handleListGeminiModels = async () => {
        try {
            setIsListingGeminiModels(true);
            setGeminiModelsList(null);
            setError(null);
            
            const token = localStorage.getItem('token');
            const response = await axios.get(`${config.backendUrl}admin/test/gemini/models`, {
                headers: { 'x-auth-token': token }
            });

            setGeminiModelsList(response.data);
        } catch (err: any) {
            setGeminiModelsList({
                success: false,
                message: 'Failed to list Gemini models',
                error: err.response?.data?.error || err.message,
                errorOutput: err.response?.data?.errorOutput || ''
            });
        } finally {
            setIsListingGeminiModels(false);
        }
    };

    const handleTestGemini = async () => {
        try {
            setIsTestingGemini(true);
            setGeminiTestResults(null);
            setError(null);
            
            const token = localStorage.getItem('token');
            const response = await axios.get(`${config.backendUrl}admin/test/gemini`, {
                headers: { 'x-auth-token': token }
            });

            setGeminiTestResults(response.data);
        } catch (err: any) {
            setGeminiTestResults({
                success: false,
                message: 'Failed to test Gemini API',
                error: err.response?.data?.error || err.message,
                errorOutput: err.response?.data?.errorOutput || ''
            });
        } finally {
            setIsTestingGemini(false);
        }
    };

    const handleMigrateLegacySponsors = async () => {
        if (!window.confirm(
            "This will update all sponsors with status 'approved' to 'previouslyApproved' in sponsornews.\n\nThis is a one-time migration. Continue?"
        )) {
            return;
        }

        try {
            setIsMigratingLegacySponsors(true);
            setLegacyMigrationResults(null);
            setError(null);

            const token = localStorage.getItem('token');
            const response = await axios.post(`${config.backendUrl}admin/migrate-legacy-sponsors`, {}, {
                headers: { 'x-auth-token': token }
            });

            setLegacyMigrationResults(response.data.results);

            setTimeout(() => {
                fetchData();
            }, 1000);
        } catch (err: any) {
            setError('Failed to run legacy sponsor migration: ' + (err.response?.data?.message || err.message));
        } finally {
            setIsMigratingLegacySponsors(false);
        }
    };

    const handleRunHunterBackfill = async () => {
        if (!window.confirm(
            'Run Hunter backfill on previouslyApproved, needs_enrichment, and hunterFailed sponsors using remaining monthly finder credits?'
        )) {
            return;
        }

        try {
            setIsRunningHunterBackfill(true);
            setError(null);

            const token = localStorage.getItem('token');
            const response = await axios.post(`${config.backendUrl}admin/hunter-backfill`, {}, {
                headers: { 'x-auth-token': token }
            });

            const { message, processed, approved, failed, quotaExhausted } = response.data;

            if (processed === 0) {
                setHunterToast({
                    type: 'warning',
                    message: message || 'No eligible sponsors for backfill. Run Legacy Migration first.',
                });
            } else {
                const quotaNote = quotaExhausted ? ' (quota exhausted)' : '';
                setHunterToast({
                    type: failed > 0 && approved === 0 ? 'warning' : 'success',
                    message: message || `Backfill complete: ${processed} processed, ${approved} approved, ${failed} failed${quotaNote}`,
                });
            }

            setTimeout(() => {
                fetchData();
            }, 1000);
        } catch (err: any) {
            setError('Hunter backfill failed: ' + (err.response?.data?.message || err.message));
        } finally {
            setIsRunningHunterBackfill(false);
        }
    };

    const handleExportLegacyEmails = async () => {
        try {
            setIsExportingLegacyEmails(true);
            setError(null);

            const token = localStorage.getItem('token');
            const response = await axios.get(`${config.backendUrl}admin/export-legacy-emails`, {
                headers: { 'x-auth-token': token },
                responseType: 'blob',
            });

            const countHeader = response.headers['x-email-count'];
            const text = await response.data.text();
            const emailCount = countHeader
                ? Number(countHeader)
                : text.split('\n').filter((line: string) => line.trim()).length;

            const blob = new Blob([text], { type: 'text/plain' });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'legacy-emails.txt';
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            setHunterToast({
                type: 'success',
                message: `${emailCount} emails exported for verification`,
            });
        } catch (err: any) {
            setError('Failed to export legacy emails: ' + (err.response?.data?.message || err.message));
        } finally {
            setIsExportingLegacyEmails(false);
        }
    };

    const handleIngestHunterResults = async () => {
        if (!window.confirm(
            'Import Hunter bulk verification results from server/scripts/hunter-results.csv and update sponsor statuses?'
        )) {
            return;
        }

        try {
            setIsIngestingHunterResults(true);
            setError(null);

            const token = localStorage.getItem('token');
            const response = await axios.post(`${config.backendUrl}admin/ingest-hunter-verification`, {}, {
                headers: { 'x-auth-token': token },
            });

            const summary = response.data?.summary;
            if (summary) {
                const toastType = summary.totalApproved > 0 ? 'success' : 'warning';
                setHunterToast({
                    type: toastType,
                    message: `${summary.totalApproved} approved, ${summary.totalHunterFailed} failed, ${summary.totalUnmatched} unmatched (${summary.totalRowsProcessed} rows)`,
                });
            } else {
                setHunterToast({
                    type: 'success',
                    message: response.data?.message || 'Hunter results ingested',
                });
            }

            setTimeout(() => {
                fetchData();
            }, 1000);
        } catch (err: any) {
            setError('Hunter ingestion failed: ' + (err.response?.data?.message || err.message));
        } finally {
            setIsIngestingHunterResults(false);
        }
    };

    const handleFixNewsletterData = async () => {
        if (!window.confirm('This will migrate missing newsletter data from the old Sponsor collection to SponsorNew. Continue?')) {
            return;
        }

        try {
            setIsFixingNewsletterData(true);
            setNewsletterFixResults(null);
            setError(null);
            
            const token = localStorage.getItem('token');
            const response = await axios.post(`${config.backendUrl}migration/fix-newsletter-data`, {}, {
                headers: { 'x-auth-token': token }
            });

            setNewsletterFixResults(response.data.results);
            
            setTimeout(() => {
                fetchData();
            }, 1000);
        } catch (err: any) {
            setError('Failed to fix newsletter data: ' + (err.response?.data?.message || err.message));
        } finally {
            setIsFixingNewsletterData(false);
        }
    };

    // Handle bulk actions
    const handleBulkAction = async (action: string) => {
        if (selectedSponsors.length === 0) return;
        
        try {
            setIsPerformingBulkAction(true);
            const token = localStorage.getItem('token');

            // Casual bulk "Reject" is now reversible (soft-reject: just
            // flips status) — the permanent delete-and-blacklist-domain
            // version is a separate, explicit, single-sponsor action only
            // reachable from inside the edit modal (see handleSponsorAction
            // / EditSponsorModal's Delete & Blacklist Domain button), so a
            // multi-select misclick can't destroy data anymore.
            const effectiveAction = action === 'reject' ? 'soft-reject' : action;

            const response = await axios.post(`${config.backendUrl}admin/sponsors/bulk-action`, {
                action: effectiveAction,
                sponsorIds: selectedSponsors
            }, {
                headers: { 'x-auth-token': token }
            });

            if (action === 'approve') {
                showHunterApprovalToasts(response.data?.hunterResults);
            }
            
            setSelectedSponsors([]);
            // Small delay to ensure database operations complete before refetching
            setTimeout(() => {
                fetchData();
            }, 100);
        } catch (err: any) {
            setError(`Failed to ${action} sponsors`);
        } finally {
            setIsPerformingBulkAction(false);
        }
    };

    // Handle individual sponsor actions
    const handleSponsorAction = async (sponsorId: string, action: string) => {
        try {
            setIsPerformingIndividualAction(true);
            const token = localStorage.getItem('token');
            
            if (action === 'reject') {
                // For reject action, show confirmation dialog
                const sponsor = sponsors.find(s => s._id === sponsorId);
                if (sponsor) {
                    const confirmed = window.confirm(
                        `Reject and block ${sponsor.rootDomain} forever?\n\nThis will:\n- Permanently block the domain from future scraping\n- Remove the sponsor from the database\n\nThis action cannot be undone.`
                    );
                    
                    if (!confirmed) {
                        setIsPerformingIndividualAction(false);
                        return;
                    }
                    
                    // Use bulk-action endpoint which handles blacklisting and deletion
                    await axios.post(`${config.backendUrl}admin/sponsors/bulk-action`, {
                        action: 'reject',
                        sponsorIds: [sponsorId]
                    }, {
                        headers: { 'x-auth-token': token }
                    });
                }
            } else {
                const response = await axios.post(`${config.backendUrl}admin/sponsors/bulk-action`, {
                    action,
                    sponsorIds: [sponsorId]
                }, {
                    headers: { 'x-auth-token': token }
                });

                if (action === 'approve') {
                    showHunterApprovalToasts(response.data?.hunterResults);
                }
            }
            
            fetchData();
        } catch (err: any) {
            setError(`Failed to ${action} sponsor: ${err.message}`);
        } finally {
            setIsPerformingIndividualAction(false);
        }
    };

    // Handle edit sponsor
    const handleEditSponsor = (sponsor: Sponsor) => {
        setEditingSponsor(sponsor);
    };

    const handleQuickApprove = (e: React.MouseEvent, sponsorId: string) => {
        e.stopPropagation();
        handleSponsorAction(sponsorId, 'approve');
    };

    // Run Hunter's Finder + Verifier on a single sponsor. If Hunter returns a
    // verified, deliverable email, the backend auto-approves the sponsor with
    // that contact — this just refreshes the row afterward either way.
    const handleRunHunterForSponsor = async (e: React.MouseEvent, sponsorId: string) => {
        e.stopPropagation();
        try {
            setRunningHunterSponsorId(sponsorId);
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${config.backendUrl}admin/sponsors/${sponsorId}/run-hunter`,
                {},
                { headers: { 'x-auth-token': token } }
            );

            const result = response.data;
            if (result.success) {
                setHunterToast({ type: 'success', message: `${result.sponsorName}: contact found and approved` });
            } else {
                setHunterToast({
                    type: 'warning',
                    message: `${result.sponsorName || 'Sponsor'}: ${result.reason === 'finder_quota_exhausted' || result.reason === 'verifier_quota_exhausted'
                        ? "Hunter's quota is exhausted for this billing period"
                        : 'no verified contact found'}`,
                });
            }

            fetchData();
        } catch (err: any) {
            setError('Run Hunter failed: ' + (err.response?.data?.message || err.message));
        } finally {
            setRunningHunterSponsorId(null);
        }
    };

    // Handle save sponsor - updates sponsor with collapsed view
    const handleSaveSponsor = async (updatedSponsor: Sponsor) => {
        try {
            const token = localStorage.getItem('token');
            
            // Build payload for collapsed sponsor update
            const payload: any = {
                sponsorName: updatedSponsor.sponsorName,
                sponsorLink: updatedSponsor.sponsorLink,
                rootDomain: updatedSponsor.rootDomain,
                tags: updatedSponsor.tags,
                sponsorEmail: updatedSponsor.sponsorEmail,
                businessContact: updatedSponsor.businessContact,
                contactMethod: updatedSponsor.contactMethod,
                status: updatedSponsor.status
            };
            
            // If newslettersSponsored exists, update it; otherwise use single newsletter fields
            if (updatedSponsor.newslettersSponsored && updatedSponsor.newslettersSponsored.length > 0) {
                payload.newslettersSponsored = updatedSponsor.newslettersSponsored;
            } else {
                // Fallback: update single newsletterSponsored field
                payload.newsletterSponsored = updatedSponsor.newsletterSponsored;
                payload.subscriberCount = updatedSponsor.subscriberCount;
            }
            
            // Send update
            const response = await axios.put(`${config.backendUrl}sponsors/${updatedSponsor._id}`, payload, {
                headers: { 'x-auth-token': token }
            });

            if (response.status === 200) {
                // Refresh data to get updated collapsed records
                fetchData();
                setEditingSponsor(null);
            }
        } catch (err: any) {
            const validationDetail = err.response?.data?.details || err.response?.data?.error;
            setError(
                'Failed to save sponsor changes: ' +
                (validationDetail || err.response?.data?.message || err.message)
            );
        }
    };

    // Handle convert sponsor to affiliate
    const handleConvertToAffiliate = async (sponsorId: string) => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post(
                `${config.backendUrl}admin/sponsors/${sponsorId}/convert-to-affiliate`,
                {},
                {
                    headers: { 'x-auth-token': token }
                }
            );
            
            if (response.data.success) {
                // Refresh sponsors list
                fetchData();
                setEditingSponsor(null);
            }
        } catch (err: any) {
            throw new Error(err.response?.data?.message || 'Failed to convert sponsor to affiliate');
        }
    };

    // Fetch affiliates
    const fetchAffiliates = useCallback(async () => {
        try {
            setAffiliatesLoading(true);
            const token = localStorage.getItem('token');
            if (!token) {
                setError('Not authenticated');
                return;
            }

            const queryParams = new URLSearchParams({
                page: affiliatesPage.toString(),
                limit: '200',
                sortBy: 'dateAdded',
                sortOrder: 'desc',
                search: affiliatesSearchTerm,
                status: affiliatesStatusFilter === 'all' ? '' : affiliatesStatusFilter
            });

            const response = await axios.get(`${config.backendUrl}admin/affiliates/all?${queryParams}`, {
                headers: { 'x-auth-token': token }
            });

            setAffiliates(response.data.affiliates || []);
        } catch (err: any) {
            setError('Failed to load affiliates');
        } finally {
            setAffiliatesLoading(false);
        }
    }, [affiliatesPage, affiliatesSearchTerm, affiliatesStatusFilter]);

    // Handle close edit modal
    const handleCloseEditModal = () => {
        setEditingSponsor(null);
    };

    // The only remaining path to permanent delete + domain blacklist —
    // reuses handleSponsorAction's existing 'reject' branch (confirm
    // dialog included), then closes the modal and refetches.
    const handleDeleteAndBlacklistSponsor = async (sponsorId: string) => {
        await handleSponsorAction(sponsorId, 'reject');
        setEditingSponsor(null);
    };

    const fetchUserAnalytics = useCallback(async () => {
        try {
            setLoadingAnalytics(true);
            const token = localStorage.getItem('token');
            const response = await axios.get(`${config.backendUrl}admin/user-analytics`, {
                headers: { 'x-auth-token': token }
            });
            if (response.data.success) {
                setUserAnalytics(response.data);
            }
        } catch (error) {
            setError('Failed to load user analytics');
        } finally {
            setLoadingAnalytics(false);
        }
    }, []);

    useEffect(() => {
        if (activeTab === 'analytics' && !userAnalytics) {
            fetchUserAnalytics();
        }
    }, [activeTab, userAnalytics, fetchUserAnalytics]);

    // Handle selection
    const handleSelectAll = () => {
        if (selectedSponsors.length === sponsors.length) {
            setSelectedSponsors([]);
        } else {
            setSelectedSponsors(sponsors.map(s => s._id));
        }
    };

    // Toggle sponsor row expansion
    const toggleSponsorExpansion = (sponsorId: string) => {
        setExpandedSponsors(prev => {
            const newSet = new Set(prev);
            if (newSet.has(sponsorId)) {
                newSet.delete(sponsorId);
            } else {
                newSet.add(sponsorId);
            }
            return newSet;
        });
    };

    const handleSelectSponsor = (sponsorId: string) => {
        setSelectedSponsors(prev => 
            prev.includes(sponsorId) 
                ? prev.filter(id => id !== sponsorId)
                : [...prev, sponsorId]
        );
    };


    // Get status badge info
    const getStatusBadge = (sponsor: Sponsor) => {
        if (sponsor.status === 'approved') {
            return { text: 'Approved', color: '#10b981', icon: faCheckCircle };
        }
        if (sponsor.status === 'previouslyApproved') {
            return { text: 'Previously Approved', color: '#6366f1', icon: faCheckCircle };
        }
        if (sponsor.status === 'hunterFailed') {
            return { text: 'Hunter Failed', color: '#ef4444', icon: faExclamationTriangle };
        }
        if (sponsor.status === 'needs_review') {
            return { text: 'Needs Review', color: '#eab308', icon: faExclamationTriangle };
        }
        if (sponsor.status === 'rejected') {
            return { text: 'Rejected', color: '#ef4444', icon: faTimesIcon };
        }
        return { text: 'Pending', color: '#f59e0b', icon: faClock };
    };

    // Helper function to check if sponsor has contact info (matches consolidation script logic)
    const hasContactInfo = (sponsor: Sponsor) => {
        const hasEmail = sponsor.sponsorEmail && sponsor.sponsorEmail.trim() !== '';
        const hasBusinessContact = sponsor.businessContact && sponsor.businessContact.trim() !== '';
        
        return hasEmail || hasBusinessContact;
    };

    // Get contact info display with detailed formatting
    const getContactDisplay = (sponsor: Sponsor) => {
        // If we have detailed contact info from Gemini (named person)
        if (sponsor.contactPersonName && sponsor.contactPersonTitle && sponsor.sponsorEmail) {
            return (
                <div className="contact-detailed">
                    <div className="contact-person">
                        <div className="contact-name">{sponsor.contactPersonName}</div>
                        <div className="contact-title">{sponsor.contactPersonTitle}</div>
                    </div>
                    <div className="contact-email">
                        <FontAwesomeIcon icon={faEnvelope} />
                        <a href={`mailto:${sponsor.sponsorEmail}`}>{sponsor.sponsorEmail}</a>
                    </div>
                    {sponsor.contactType === 'named_person' && (
                        <span className="contact-badge contact-badge-high">High Value Contact</span>
                    )}
                </div>
            );
        }
        
        // If we have business email type
        if (sponsor.contactType === 'business_email' && sponsor.sponsorEmail) {
            return (
                <div className="contact-detailed">
                    <div className="contact-email">
                        <FontAwesomeIcon icon={faEnvelope} />
                        <a href={`mailto:${sponsor.sponsorEmail}`}>{sponsor.sponsorEmail}</a>
                    </div>
                    <span className="contact-badge contact-badge-medium">Business Email</span>
                </div>
            );
        }
        
        // If we have generic email
        if (sponsor.contactType === 'generic_email' && sponsor.sponsorEmail) {
            return (
                <div className="contact-detailed">
                    <div className="contact-email">
                        <FontAwesomeIcon icon={faEnvelope} />
                        <a href={`mailto:${sponsor.sponsorEmail}`}>{sponsor.sponsorEmail}</a>
                    </div>
                    <span className="contact-badge contact-badge-low">Generic Email</span>
                </div>
            );
        }
        
        // Fallback to simple email display
        const hasEmail = sponsor.sponsorEmail && sponsor.sponsorEmail.trim();
        const hasBusinessContact = sponsor.businessContact && sponsor.businessContact.trim();
        
        if (hasEmail && hasBusinessContact) {
            return (
                <div className="contact-both">
                    <div className="contact-item">
                        <FontAwesomeIcon icon={faEnvelope} />
                        <a href={`mailto:${sponsor.sponsorEmail}`}>{sponsor.sponsorEmail}</a>
                    </div>
                    <div className="contact-item">
                        <FontAwesomeIcon icon={faEnvelope} />
                        <a href={`mailto:${sponsor.businessContact}`}>{sponsor.businessContact}</a>
                    </div>
                </div>
            );
        } else if (hasEmail) {
            return (
                <div className="contact-item">
                    <FontAwesomeIcon icon={faEnvelope} />
                    <a href={`mailto:${sponsor.sponsorEmail}`}>{sponsor.sponsorEmail}</a>
                </div>
            );
        } else if (hasBusinessContact) {
            return (
                <div className="contact-detailed">
                    <div className="contact-item">
                        <FontAwesomeIcon icon={faEnvelope} />
                        <a href={`mailto:${sponsor.businessContact}`}>{sponsor.businessContact}</a>
                    </div>
                    {sponsor.emailVerified && (
                        <span className="contact-badge contact-badge-high">Hunter Verified</span>
                    )}
                    {typeof sponsor.hunterConfidence === 'number' && (
                        <span className="contact-badge contact-badge-medium">
                            Hunter {sponsor.hunterConfidence}% confidence
                        </span>
                    )}
                </div>
            );
        } else {
            return <span className="no-contact">No contact</span>;
        }
    };

    // Format date
    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    // Tag heat map for whatever's currently filtered in the Sponsors tab
    // (matches the "N" count already shown on the active filter pill) — a
    // sponsor can carry multiple tags, so percentages sum to more than
    // 100%; each is just "share of currently-filtered records carrying
    // this tag", same normalizeTags logic used everywhere else so this
    // always agrees with what filtering by that tag would actually show.
    const tagBreakdown = useMemo(() => {
        const counts: Record<string, number> = {};
        sponsors.forEach((s) => {
            normalizeTags(s.tags).forEach((tag) => {
                counts[tag] = (counts[tag] || 0) + 1;
            });
        });
        const total = sponsors.length;
        const entries = Object.entries(counts)
            .map(([tag, count]) => ({ tag, count, pct: total > 0 ? (count / total) * 100 : 0 }))
            .sort((a, b) => b.count - a.count);
        const maxPct = entries.length > 0 ? entries[0].pct : 0;
        return { entries, total, maxPct };
    }, [sponsors]);

    if (loading && !stats) {
        return (
            <div className="admin-loading">
                <div className="admin-loading__spinner"></div>
                <p>Loading Admin Dashboard...</p>
            </div>
        );
    }

    return (
        <div className="admin-page">
            <div className="admin-dashboard page-shell page-shell--wide">
                <header className="page-header admin-page-header">
                    <h1 className="page-title">Admin Dashboard</h1>
                    <p className="page-lead">Manage sponsors, newsletters, and user analytics</p>
                </header>

                {/* Tab Navigation */}
                <div className="admin-tabs">
                    <button
                        className={`admin-tab ${activeTab === 'overview' ? 'active' : ''}`}
                        onClick={() => setActiveTab('overview')}
                    >
                        <FontAwesomeIcon icon={faChartLine} />
                        Overview
                    </button>
                    <button
                        className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
                        onClick={() => setActiveTab('users')}
                    >
                        <FontAwesomeIcon icon={faUsers} />
                        Users
                    </button>
                    <button
                        className={`admin-tab ${activeTab === 'sponsors' ? 'active' : ''}`}
                        onClick={() => setActiveTab('sponsors')}
                    >
                        <FontAwesomeIcon icon={faDatabase} />
                        Sponsors
                    </button>
                    <button 
                        className={`admin-tab ${activeTab === 'analytics' ? 'active' : ''}`}
                        onClick={() => setActiveTab('analytics')}
                    >
                        <FontAwesomeIcon icon={faChartLine} />
                        Analytics
                    </button>
                    <button
                        className={`admin-tab ${activeTab === 'social' ? 'active' : ''}`}
                        onClick={() => setActiveTab('social')}
                    >
                        <FontAwesomeIcon icon={faBullhorn} />
                        Social
                    </button>
                </div>

                {/* Overview Tab */}
                {activeTab === 'overview' && (
                    <div className="admin-tab-content">
                        {overviewLoading || !overview ? (
                            <div className="admin-loading">
                                <div className="admin-loading__spinner"></div>
                                <p>Loading Overview...</p>
                            </div>
                        ) : (
                            <>
                                {new Date().getDay() === 0 && !sundayReminderDismissed && (
                                    <div className="sunday-reminder-banner">
                                        <span>📬 It's Sunday — send this week's newsletter post?</span>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                localStorage.setItem('sundayReminderDismissedDate', new Date().toDateString());
                                                setSundayReminderDismissed(true);
                                            }}
                                        >
                                            Dismiss
                                        </button>
                                    </div>
                                )}
                                <div className="stats-grid">
                                    <div className="stat-card stat-card--highlight">
                                        <div className="stat-card__value">${overview.users.mrr}</div>
                                        <div className="stat-card__label">MRR</div>
                                    </div>
                                    <div className="stat-card">
                                        <div className="stat-card__value">{overview.users.paying}</div>
                                        <div className="stat-card__label">Paying Users</div>
                                    </div>
                                    <div className="stat-card">
                                        <div className="stat-card__value">{overview.users.trialing}</div>
                                        <div className="stat-card__label">On Trial</div>
                                    </div>
                                    <div className="stat-card">
                                        <div className="stat-card__value">{overview.users.total}</div>
                                        <div className="stat-card__label">Total Users</div>
                                    </div>
                                    <div className="stat-card">
                                        <div className="stat-card__value">{overview.users.newThisWeek}</div>
                                        <div className="stat-card__label">Signups This Week</div>
                                    </div>
                                    <div className="stat-card">
                                        <div className="stat-card__value">{overview.sponsors.live}</div>
                                        <div className="stat-card__label">Live Sponsors</div>
                                    </div>
                                    <div className="stat-card">
                                        <div className="stat-card__value">{overview.sponsors.newThisWeek}</div>
                                        <div className="stat-card__label">Sponsors Added This Week</div>
                                    </div>
                                </div>

                                <div className="overview-panels">
                                    <div className="panel overview-panel">
                                        <h3>Sponsor Pipeline</h3>
                                        <div className="pipeline-rows">
                                            {['pending', 'needs_review', 'needs_enrichment', 'hunterFailed', 'previouslyApproved', 'approved', 'rejected'].map((status) => (
                                                <div key={status} className="pipeline-row">
                                                    <span className="pipeline-row__label">{status.replace(/_/g, ' ')}</span>
                                                    <span className="pipeline-row__count">{overview.sponsors.pipeline[status] || 0}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>

                                    <div className="panel overview-panel">
                                        <h3>Hunter Quota{overview.hunter?.planName ? ` (${overview.hunter.planName})` : ''}</h3>
                                        {overview.hunter ? (
                                            <>
                                                <div className="pipeline-rows">
                                                    <div className="pipeline-row">
                                                        <span className="pipeline-row__label">Credits remaining</span>
                                                        <span className="pipeline-row__count">
                                                            {overview.hunter.creditsRemaining}
                                                            {overview.hunter.creditsAvailable ? ` / ${overview.hunter.creditsAvailable}` : ''}
                                                        </span>
                                                    </div>
                                                    {overview.hunter.resetDate && (
                                                        <div className="pipeline-row">
                                                            <span className="pipeline-row__label">Resets</span>
                                                            <span className="pipeline-row__count">{formatDate(overview.hunter.resetDate)}</span>
                                                        </div>
                                                    )}
                                                </div>
                                                <p className="overview-panel__hint">Finder and verifier calls share this one pool — not separate quotas.</p>
                                                {overview.hunter.creditsRemaining === 0 && (
                                                    <p className="overview-panel__warning">Quota exhausted — approvals queue as hunterFailed until it resets</p>
                                                )}
                                                {!overview.hunter.live && (
                                                    <p className="overview-panel__warning">Live check to Hunter failed — showing an estimate, not confirmed numbers</p>
                                                )}
                                            </>
                                        ) : (
                                            <p>Unable to reach Hunter right now.</p>
                                        )}
                                    </div>

                                    <div className="panel overview-panel">
                                        <h3>Newsletter Requests{newsletterRequests.filter(r => r.status === 'pending').length > 0 ? ` (${newsletterRequests.filter(r => r.status === 'pending').length} pending)` : ''}</h3>
                                        {newsletterRequestsLoading ? (
                                            <p>Loading…</p>
                                        ) : newsletterRequests.length === 0 ? (
                                            <p>No requests yet.</p>
                                        ) : (
                                            <div className="newsletter-requests-list">
                                                {newsletterRequests.slice(0, 25).map((r) => (
                                                    <div key={r._id} className={`newsletter-request-row newsletter-request-row--${r.status}`}>
                                                        <div className="newsletter-request-row__main">
                                                            <strong>{r.newsletterName}</strong>
                                                            {r.newsletterUrl && (
                                                                <a href={r.newsletterUrl.startsWith('http') ? r.newsletterUrl : `https://${r.newsletterUrl}`} target="_blank" rel="noreferrer">
                                                                    <FontAwesomeIcon icon={faExternalLink} />
                                                                </a>
                                                            )}
                                                            {r.niche && <span className="newsletter-request-row__niche">{r.niche}</span>}
                                                        </div>
                                                        <div className="newsletter-request-row__meta">
                                                            {r.requesterEmail} · {formatDate(r.dateRequested)}
                                                        </div>
                                                        {r.status === 'pending' ? (
                                                            <div className="newsletter-request-row__actions">
                                                                <button type="button" onClick={() => handleUpdateNewsletterRequestStatus(r._id, 'added')}>Mark Added</button>
                                                                <button type="button" onClick={() => handleUpdateNewsletterRequestStatus(r._id, 'declined')}>Decline</button>
                                                            </div>
                                                        ) : (
                                                            <span className="newsletter-request-row__status">{r.status}</span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>

                                    <div className="panel overview-panel">
                                        <h3>User Feedback{feedbackEntries.filter(f => f.status === 'new').length > 0 ? ` (${feedbackEntries.filter(f => f.status === 'new').length} new)` : ''}</h3>
                                        {feedbackLoading ? (
                                            <p>Loading…</p>
                                        ) : feedbackEntries.length === 0 ? (
                                            <p>No feedback yet.</p>
                                        ) : (
                                            <div className="newsletter-requests-list">
                                                {feedbackEntries.slice(0, 25).map((f) => (
                                                    <div key={f._id} className={`newsletter-request-row newsletter-request-row--${f.status === 'new' ? 'pending' : 'added'}`}>
                                                        <div className="newsletter-request-row__main">
                                                            <strong>{f.name || f.email}</strong>
                                                            {f.source === 'cancellation' && (
                                                                <span className="newsletter-request-row__niche">cancellation</span>
                                                            )}
                                                        </div>
                                                        <div className="newsletter-request-row__meta">
                                                            {f.email} · {formatDate(f.createdAt)}
                                                        </div>
                                                        <p style={{ margin: '4px 0', whiteSpace: 'pre-wrap' }}>{f.message}</p>
                                                        {f.status === 'new' ? (
                                                            <div className="newsletter-request-row__actions">
                                                                <button type="button" onClick={() => handleMarkFeedbackReviewed(f._id)}>Mark Reviewed</button>
                                                            </div>
                                                        ) : (
                                                            <span className="newsletter-request-row__status">{f.status}</span>
                                                        )}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                )}

                {/* Users Tab */}
                {activeTab === 'users' && (
                    <div className="admin-tab-content">
                        <div className="search-filter-bar">
                            <div className="search-input-container">
                                <FontAwesomeIcon icon={faSearch} className="search-icon" />
                                <input
                                    type="text"
                                    placeholder="Search by email..."
                                    value={userSearch}
                                    onChange={(e) => setUserSearch(e.target.value)}
                                    onKeyDown={(e) => { if (e.key === 'Enter') fetchAdminUsers(userSearch); }}
                                    className="search-input"
                                />
                            </div>
                            <button className="btn btn-secondary btn-sm" onClick={() => fetchAdminUsers(userSearch)}>
                                Search
                            </button>
                        </div>

                        {adminUsersLoading ? (
                            <div className="admin-loading">
                                <div className="admin-loading__spinner"></div>
                                <p>Loading Users...</p>
                            </div>
                        ) : (
                            <div className="sponsor-table-container">
                                <table className="sponsor-table admin-sponsors-table admin-users-table">
                                    <thead>
                                        <tr>
                                            <th>Email</th>
                                            <th>Access</th>
                                            <th>Signed Up</th>
                                            <th>Trial Ends</th>
                                            <th>Email Stage</th>
                                            <th>Newsletter</th>
                                            <th>Viewed</th>
                                            <th>Applied</th>
                                            <th>Contacted</th>
                                            <th>Responded</th>
                                            <th>Deals Closed</th>
                                            <th>Revenue Reported</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {adminUsers.map((u, index) => {
                                            const prevGroup = index > 0 ? adminUsers[index - 1].sortGroup : null;
                                            const showGroupHeader = u.sortGroup !== prevGroup;
                                            const groupLabel = USER_GROUP_LABELS[u.sortGroup] || 'Other';

                                            return (
                                                <React.Fragment key={u._id}>
                                                    {showGroupHeader && (
                                                        <tr className="admin-users-group-header">
                                                            <td colSpan={12}>{groupLabel}</td>
                                                        </tr>
                                                    )}
                                                    <tr className={u.accessLevel === 'paying' ? 'user-row--paying' : ''}>
                                                        <td>
                                                            <div className="user-email-cell">
                                                                {u.email}
                                                                {u.name && <span className="user-name-sub">{u.name}</span>}
                                                            </div>
                                                        </td>
                                                        <td>
                                                            <span className={`access-badge access-badge--${u.accessLevel}`}>
                                                                {u.accessLevel}
                                                                {u.cancelAtPeriodEnd ? ' (cancelling)' : ''}
                                                            </span>
                                                        </td>
                                                        <td>{formatDate(u.signedUpAt)}</td>
                                                        <td>{u.trialEnd ? formatDate(u.trialEnd) : '—'}</td>
                                                        <td>
                                                            <span className="onboarding-stage">
                                                                {u.emailUnsubscribed ? 'unsubscribed' : u.onboardingStage}
                                                            </span>
                                                        </td>
                                                        <td>
                                                            {u.newsletterName
                                                                ? `${u.newsletterName}${u.newsletterAudience ? ` (${u.newsletterAudience.toLocaleString()})` : ''}`
                                                                : '—'}
                                                        </td>
                                                        <td>{u.sponsorsViewed}</td>
                                                        <td>{u.sponsorsApplied}</td>
                                                        <td>{u.sponsorsContacted}</td>
                                                        <td>{u.sponsorsResponded}</td>
                                                        <td>
                                                            {u.sponsorDealsClosed > 0 ? (
                                                                <span className="deals-closed-badge">{u.sponsorDealsClosed}</span>
                                                            ) : 0}
                                                        </td>
                                                        <td>
                                                            {u.sponsorRevenue > 0 ? `$${u.sponsorRevenue.toLocaleString()}` : '—'}
                                                        </td>
                                                    </tr>
                                                </React.Fragment>
                                            );
                                        })}
                                    </tbody>
                                </table>
                                {adminUsers.length === 0 && (
                                    <div className="empty-state">
                                        <FontAwesomeIcon icon={faUsers} />
                                        <h3>No users found</h3>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                )}

                {/* Sponsors Tab — rebuilt to match what a customer sees
                    (see PaidSponsorTable's placement cards), just with
                    admin actions instead of contact/pitch buttons. Vetting
                    Agent's status panel is gone from here per request
                    ("dont need to see the vetting agent stuff") — its
                    trigger lives in the Tools tray below since it's still
                    a useful action, just not a daily-glance panel. */}
                {activeTab === 'sponsors' && (
                    <div className="admin-tab-content admin-sponsors-tab">
                        {tagBreakdown.total > 0 && (
                            <div
                                className="tag-heatmap-strip"
                                title={`Share of the ${tagBreakdown.total.toLocaleString()} currently-filtered records carrying each tag — a record can carry more than one, so this sums past 100%.`}
                            >
                                {tagBreakdown.entries.map(({ tag, count, pct }) => {
                                    const intensity = tagBreakdown.maxPct > 0 ? pct / tagBreakdown.maxPct : 0;
                                    return (
                                        <span
                                            key={tag}
                                            className="tag-heatmap-strip__cell"
                                            style={{ background: `rgba(37, 99, 235, ${(0.08 + intensity * 0.42).toFixed(2)})` }}
                                        >
                                            <span className="tag-heatmap-strip__pct">{pct.toFixed(0)}%</span>
                                            {tag}
                                            <span className="tag-heatmap-strip__count">({count})</span>
                                        </span>
                                    );
                                })}
                            </div>
                        )}

                        <div className="sponsors-toolbar">
                            <div className="sponsors-toolbar__search">
                                <FontAwesomeIcon icon={faSearch} />
                                <input
                                    type="text"
                                    placeholder="Search sponsors, newsletters, domains..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </div>
                            <button
                                type="button"
                                className="sponsors-toolbar__tools-toggle"
                                onClick={() => setToolsExpanded((v) => !v)}
                                aria-expanded={toolsExpanded}
                            >
                                Tools
                                <FontAwesomeIcon icon={toolsExpanded ? faChevronUp : faChevronDown} />
                            </button>
                        </div>

                        {toolsExpanded && (
                            <div className="sponsors-tools-tray">
                                <button type="button" className="btn btn-secondary btn-sm" onClick={fetchData} disabled={loading}>
                                    <FontAwesomeIcon icon={faRefresh} spin={loading} />
                                    Refresh
                                </button>
                                <button type="button" className="btn btn-secondary btn-sm" onClick={handleRunScraper} disabled={isRunningScraper}>
                                    <FontAwesomeIcon icon={isRunningScraper ? faSpinner : faRobot} spin={isRunningScraper} />
                                    {isRunningScraper ? 'Running…' : 'Run Scraper'}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={handleRunVettingAgent}
                                    disabled={isRunningVettingAgent || !vettingAgentStatus?.agentConfigured}
                                    title="Gemini rejects junk, flags uncertain, Hunter-approves real sponsors"
                                >
                                    <FontAwesomeIcon icon={isRunningVettingAgent ? faSpinner : faRobot} spin={isRunningVettingAgent} />
                                    {isRunningVettingAgent ? 'Running…' : 'Run Vetting Agent'}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={handleMigrateLegacySponsors}
                                    disabled={isMigratingLegacySponsors}
                                    title="One-time update: approved → previouslyApproved"
                                >
                                    <FontAwesomeIcon icon={isMigratingLegacySponsors ? faSpinner : faDatabase} spin={isMigratingLegacySponsors} />
                                    {isMigratingLegacySponsors ? 'Migrating…' : 'Legacy Migration'}
                                </button>
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={handleRunHunterBackfill}
                                    disabled={isRunningHunterBackfill}
                                    title="Process previouslyApproved / needs_enrichment / hunterFailed with finder credits"
                                >
                                    <FontAwesomeIcon icon={isRunningHunterBackfill ? faSpinner : faEnvelope} spin={isRunningHunterBackfill} />
                                    {isRunningHunterBackfill ? 'Running…' : 'Hunter Backfill'}
                                </button>
                                <button type="button" className="btn btn-secondary btn-sm" onClick={handleExportLegacyEmails} disabled={isExportingLegacyEmails}>
                                    <FontAwesomeIcon icon={isExportingLegacyEmails ? faSpinner : faEnvelope} spin={isExportingLegacyEmails} />
                                    {isExportingLegacyEmails ? 'Exporting…' : 'Export Legacy Emails'}
                                </button>
                                <button type="button" className="btn btn-secondary btn-sm" onClick={handleIngestHunterResults} disabled={isIngestingHunterResults}>
                                    <FontAwesomeIcon icon={isIngestingHunterResults ? faSpinner : faCheckCircle} spin={isIngestingHunterResults} />
                                    {isIngestingHunterResults ? 'Importing…' : 'Ingest Hunter Results'}
                                </button>
                                {vettingAgentMessage && <p className="sponsors-tools-tray__message">{vettingAgentMessage}</p>}
                            </div>
                        )}

                        <div className="sponsors-filter-pills">
                            {SPONSOR_FILTERS.map((f) => (
                                <button
                                    key={f.key}
                                    type="button"
                                    className={`sponsors-filter-pill ${statusFilter === f.key ? 'active' : ''}`}
                                    onClick={() => setStatusFilter(f.key)}
                                >
                                    {f.label}
                                    {statusFilter === f.key && sponsors.length > 0 && (
                                        <span className="sponsors-filter-pill__count">{sponsors.length}</span>
                                    )}
                                </button>
                            ))}
                        </div>

                        {selectedSponsors.length > 0 && (
                            <div className="sponsors-bulk-bar">
                                <span className="sponsors-bulk-bar__count">{selectedSponsors.length} selected</span>
                                <button className="btn btn-success btn-sm" onClick={() => handleBulkAction('approve')} disabled={isPerformingBulkAction}>
                                    <FontAwesomeIcon icon={faCheck} />
                                    Approve
                                </button>
                                <button className="btn btn-danger btn-sm" onClick={() => handleBulkAction('reject')} disabled={isPerformingBulkAction}>
                                    <FontAwesomeIcon icon={faTimesIcon} />
                                    Reject
                                </button>
                                <button className="btn btn-secondary btn-sm" onClick={() => setSelectedSponsors([])}>
                                    Clear
                                </button>
                            </div>
                        )}

                        {sponsors.length > 0 && (
                            <label className="sponsors-select-all">
                                <input
                                    type="checkbox"
                                    checked={selectedSponsors.length === sponsors.length && sponsors.length > 0}
                                    onChange={handleSelectAll}
                                />
                                Select all {sponsors.length}
                            </label>
                        )}

                        <div className="admin-sponsor-grid">
                            {sponsors.map((sponsor) => {
                                const statusBadge = getStatusBadge(sponsor);
                                const isExpanded = expandedSponsors.has(sponsor._id);
                                const newsletters = sponsor.newslettersSponsored || [];

                                return (
                                    <div key={sponsor._id} className="admin-sponsor-card">
                                        <div className="admin-sponsor-card__top">
                                            <input
                                                type="checkbox"
                                                className="admin-sponsor-card__checkbox"
                                                checked={selectedSponsors.includes(sponsor._id)}
                                                onChange={() => handleSelectSponsor(sponsor._id)}
                                                aria-label={`Select ${sponsor.sponsorName}`}
                                            />
                                            <CompanyLogo domain={sponsor.rootDomain} name={sponsor.sponsorName} size={36} />
                                            <div className="admin-sponsor-card__identity">
                                                <a
                                                    href={`https://${sponsor.rootDomain}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    className="admin-sponsor-card__name"
                                                >
                                                    {sponsor.sponsorName}
                                                    <FontAwesomeIcon icon={faExternalLink} />
                                                </a>
                                                <span className="admin-sponsor-card__domain">{sponsor.rootDomain}</span>
                                            </div>
                                            <span
                                                className="admin-sponsor-card__status"
                                                style={{ backgroundColor: statusBadge.color }}
                                            >
                                                <FontAwesomeIcon icon={statusBadge.icon} />
                                                {statusBadge.text}
                                            </span>
                                        </div>

                                        {sponsor.tags && sponsor.tags.length > 0 && (
                                            <div className="admin-sponsor-card__tags">
                                                {sponsor.tags.slice(0, 4).map((tag, idx) => (
                                                    <span key={idx} className="admin-sponsor-card__tag">{tag}</span>
                                                ))}
                                            </div>
                                        )}

                                        <div className="admin-sponsor-card__meta">
                                            <div className="admin-sponsor-card__contact">
                                                {getContactDisplay(sponsor)}
                                            </div>
                                            <div className="admin-sponsor-card__stats">
                                                <span>{formatDate(sponsor.dateAdded)}</span>
                                                {sponsor.totalPlacements ? (
                                                    <span>{sponsor.totalPlacements} newsletter{sponsor.totalPlacements !== 1 ? 's' : ''}</span>
                                                ) : (
                                                    <span className="admin-sponsor-card__no-placements">No placements</span>
                                                )}
                                                {newsletters.length > 0 && (
                                                    <button
                                                        type="button"
                                                        className="admin-sponsor-card__expand"
                                                        onClick={() => toggleSponsorExpansion(sponsor._id)}
                                                    >
                                                        {isExpanded ? 'Hide' : 'Show'} placements
                                                        <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
                                                    </button>
                                                )}
                                            </div>
                                        </div>

                                        {isExpanded && newsletters.length > 0 && (
                                            <div className="admin-sponsor-card__newsletters">
                                                {newsletters.map((newsletter, idx) => (
                                                    <div key={idx} className="admin-sponsor-card__newsletter-item">
                                                        <strong>{newsletter.newsletterName || 'Unnamed Newsletter'}</strong>
                                                        <span>{newsletter.estimatedAudience ? newsletter.estimatedAudience.toLocaleString() : 'N/A'} readers</span>
                                                        <span>{formatDate(newsletter.dateSponsored ? (typeof newsletter.dateSponsored === 'string' ? newsletter.dateSponsored : newsletter.dateSponsored.toString()) : sponsor.dateAdded)}</span>
                                                    </div>
                                                ))}
                                                {(sponsor.vetting_reason || sponsor.rejection_reason || sponsor.approval_reason) && (
                                                    <p className="admin-sponsor-card__vetting-note">
                                                        {sponsor.vetting_reason || sponsor.rejection_reason || sponsor.approval_reason}
                                                    </p>
                                                )}
                                            </div>
                                        )}

                                        <div className="admin-sponsor-card__actions">
                                            {(sponsor.status === 'pending' || sponsor.status === 'needs_review' || sponsor.status === 'hunterFailed' || sponsor.status === 'needs_enrichment') && (
                                                <button
                                                    type="button"
                                                    className="btn btn-success btn-sm"
                                                    onClick={(e) => handleQuickApprove(e, sponsor._id)}
                                                    disabled={isPerformingIndividualAction}
                                                >
                                                    <FontAwesomeIcon icon={faCheck} />
                                                    Approve
                                                </button>
                                            )}
                                            <button
                                                type="button"
                                                className="btn btn-secondary btn-sm"
                                                onClick={() => handleEditSponsor(sponsor)}
                                            >
                                                Edit
                                            </button>
                                            {sponsor.status !== 'approved' && sponsor.status !== 'rejected' && (
                                                <button
                                                    type="button"
                                                    className="btn btn-secondary btn-sm"
                                                    onClick={(e) => handleRunHunterForSponsor(e, sponsor._id)}
                                                    disabled={runningHunterSponsorId === sponsor._id}
                                                    title="Run Hunter's Finder + Verifier to look up a contact for this sponsor"
                                                >
                                                    <FontAwesomeIcon icon={runningHunterSponsorId === sponsor._id ? faSpinner : faSearch} spin={runningHunterSponsorId === sponsor._id} />
                                                    {runningHunterSponsorId === sponsor._id ? 'Running…' : 'Run Hunter'}
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>

                        {sponsors.length === 0 && !loading && (
                            <div className="empty-state">
                                <FontAwesomeIcon icon={faUsers} />
                                <h3>No sponsors found</h3>
                                <p>Try adjusting your search or filters</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Analytics Tab */}
                {activeTab === 'analytics' && (
                    <div className="admin-tab-content">
                        {loadingAnalytics ? (
                            <div className="admin-loading">
                                <div className="admin-loading__spinner"></div>
                                <p>Loading Analytics...</p>
                            </div>
                        ) : userAnalytics ? (
                            <>
                                <div className="analytics-header">
                                    <h2>User Analytics</h2>
                                    <button 
                                        className="btn btn-secondary"
                                        onClick={fetchUserAnalytics}
                                        disabled={loadingAnalytics}
                                    >
                                        <FontAwesomeIcon icon={faRefresh} spin={loadingAnalytics} />
                                        Refresh
                                    </button>
                                </div>

                                {/* Key Metrics */}
                                <div className="analytics-stats-grid">
                                    <div className="analytics-stat-card">
                                        <div className="analytics-stat-icon">
                                            <FontAwesomeIcon icon={faUsers} />
                                        </div>
                                        <div className="analytics-stat-content">
                                            <h3>{userAnalytics.totalUsers?.toLocaleString() || '0'}</h3>
                                            <p>Total Users</p>
                                        </div>
                                    </div>

                                    <div className="analytics-stat-card analytics-stat-card--primary">
                                        <div className="analytics-stat-icon">
                                            <FontAwesomeIcon icon={faCheckCircle} />
                                        </div>
                                        <div className="analytics-stat-content">
                                            <h3>{userAnalytics.activePayingUsers ?? userAnalytics.paidUsers ?? '0'}</h3>
                                            <p>Active (Paying)</p>
                                        </div>
                                    </div>

                                    <div className="analytics-stat-card analytics-stat-card--warning">
                                        <div className="analytics-stat-icon">
                                            <FontAwesomeIcon icon={faClock} />
                                        </div>
                                        <div className="analytics-stat-content">
                                            <h3>{userAnalytics.trialingUsers ?? '0'}</h3>
                                            <p>Trialing</p>
                                        </div>
                                    </div>
                                    
                                    <div className="analytics-stat-card">
                                        <div className="analytics-stat-icon">
                                            <FontAwesomeIcon icon={faPercent} />
                                        </div>
                                        <div className="analytics-stat-content">
                                            <h3>{userAnalytics.conversionRate || '0'}%</h3>
                                            <p>Conversion Rate</p>
                                        </div>
                                    </div>
                                    
                                    <div className="analytics-stat-card">
                                        <div className="analytics-stat-icon">
                                            <FontAwesomeIcon icon={faUsers} />
                                        </div>
                                        <div className="analytics-stat-content">
                                            <h3>{userAnalytics.usersWithoutSubscription || '0'}</h3>
                                            <p>No Subscription</p>
                                        </div>
                                    </div>
                                </div>

                                {/* Monthly Signups Chart */}
                                {userAnalytics.monthlySignups && userAnalytics.monthlySignups.length > 0 && (
                                    <div className="analytics-chart-section">
                                        <h3>Monthly User Signups</h3>
                                        <div className="analytics-chart">
                                            {userAnalytics.monthlySignups.map((month: any, index: number) => {
                                                const maxCount = Math.max(...userAnalytics.monthlySignups.map((m: any) => m.count), 1);
                                                const heightPercent = maxCount > 0 ? (month.count / maxCount) * 100 : 0;
                                                
                                                return (
                                                    <div key={index} className="analytics-chart-bar">
                                                        <div 
                                                            className="analytics-chart-bar-fill" 
                                                            style={{ height: `${Math.max(heightPercent, 5)}%` }}
                                                        ></div>
                                                        <div className="analytics-chart-bar-label">{month.month}</div>
                                                        <div className="analytics-chart-bar-value">{month.count}</div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                            </>
                        ) : (
                            <div className="analytics-empty">
                                <FontAwesomeIcon icon={faChartLine} />
                                <p>No analytics data available</p>
                            </div>
                        )}
                    </div>
                )}

                {/* Social Tab — Newsletters + the weekly Reddit/X self-post
                    generator, merged into one tab. The old Reddit-thread-
                    reply drafting workflow (paste a thread URL, get a
                    drafted reply) and the standalone human cold-outreach
                    tracker (MarketingOutreach) are both dropped per request;
                    only the self-post generator survives, moved here. */}
                {activeTab === 'social' && (
                    <div className="admin-tab-content">
                        {/* Mobile only (hidden on desktop via CSS) — desktop
                            shows all 3 columns side by side at once. */}
                        <div className="social-mobile-switcher">
                            <button
                                className={`community-subtab-btn ${communitySubTab === 'newsletters' ? 'active' : ''}`}
                                onClick={() => setCommunitySubTab('newsletters')}
                            >
                                Newsletters
                            </button>
                            <button
                                className={`community-subtab-btn ${communitySubTab === 'reddit_post' ? 'active' : ''}`}
                                onClick={() => setCommunitySubTab('reddit_post')}
                            >
                                Reddit
                            </button>
                            <button
                                className={`community-subtab-btn ${communitySubTab === 'x_post' ? 'active' : ''}`}
                                onClick={() => setCommunitySubTab('x_post')}
                            >
                                X
                            </button>
                        </div>

                        <div className="community-filter-bar">
                            {(['pending_review', 'posted', 'dismissed', 'all'] as const).map((s) => (
                                <button
                                    key={s}
                                    className={`community-filter-btn ${socialDraftStatusFilter === s ? 'active' : ''}`}
                                    onClick={() => {
                                        setSocialDraftStatusFilter(s);
                                        fetchSocialDrafts('reddit_post', s);
                                        fetchSocialDrafts('x_post', s);
                                    }}
                                >
                                    {s.replace('_', ' ')}
                                </button>
                            ))}
                        </div>
                        {socialGenerateError && <p className="draft-form__error">{socialGenerateError}</p>}

                        <div className="social-columns">
                            <div className={`social-column ${communitySubTab === 'newsletters' ? 'social-column--mobile-active' : ''}`}>
                                <h3 className="social-column__heading">Newsletters</h3>
                                <NewsletterManager />
                            </div>

                            {(['reddit_post', 'x_post'] as const).map((platform) => {
                                const drafts = platform === 'reddit_post' ? redditDrafts : xDrafts;
                                const loading = platform === 'reddit_post' ? redditDraftsLoading : xDraftsLoading;
                                return (
                                    <div
                                        key={platform}
                                        className={`social-column ${communitySubTab === platform ? 'social-column--mobile-active' : ''}`}
                                    >
                                        <h3 className="social-column__heading">{platform === 'reddit_post' ? 'Reddit' : 'X'}</h3>
                                        <div className="panel community-intro-panel">
                                            <div>
                                                <p className="vetting-agent-card__subtitle">
                                                    {platform === 'reddit_post'
                                                        ? 'An original self-post generated from your own sponsor data — not a reply, no thread discovery. Auto-generates every Wednesday.'
                                                        : 'A short post generated from your own sponsor data, hard-capped at 280 characters including the link (no X Premium). Auto-generates every Monday.'}
                                                </p>
                                            </div>
                                            <button
                                                className="btn btn-primary btn-sm"
                                                onClick={() => handleGenerateSocial(platform)}
                                                disabled={isGeneratingSocial}
                                            >
                                                {isGeneratingSocial ? 'Generating…' : 'Generate New Draft'}
                                            </button>
                                        </div>

                                        {loading ? (
                                            <div className="admin-loading">
                                                <div className="admin-loading__spinner"></div>
                                                <p>Loading drafts...</p>
                                            </div>
                                        ) : drafts.length === 0 ? (
                                            <div className="empty-state">
                                                <FontAwesomeIcon icon={faBullhorn} />
                                                <h3>No drafts here</h3>
                                                <p>
                                                    {socialDraftStatusFilter === 'pending_review'
                                                        ? 'Click "Generate New Draft" above, or wait for the next scheduled run.'
                                                        : 'Nothing in this view yet.'}
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="draft-list">
                                                {drafts.map((draft) => {
                                                    const currentBody = editedSocialBody[draft._id] ?? draft.editedBody ?? draft.body;
                                                    const isEdited = editedSocialBody[draft._id] !== undefined && editedSocialBody[draft._id] !== (draft.editedBody ?? draft.body);
                                                    const overLimit = draft.charLimit ? currentBody.length > draft.charLimit : false;
                                                    return (
                                                        <div key={draft._id} className="draft-card">
                                                            <div className="draft-card__header">
                                                                <span className="draft-card__thread-link">
                                                                    {draft.platform === 'reddit_post' ? `r/${draft.subreddit}` : 'X post'}
                                                                </span>
                                                                <span className="draft-card__date">{formatDate(draft.dateGenerated)}</span>
                                                            </div>

                                                            {draft.platform === 'reddit_post' && (
                                                                <div className="social-draft__title-row">
                                                                    <input
                                                                        type="text"
                                                                        className="draft-form__input"
                                                                        value={draft.title}
                                                                        readOnly
                                                                    />
                                                                    <button
                                                                        className="btn-sm action-btn response-btn"
                                                                        onClick={() => handleCopySocialText(`${draft._id}-title`, draft.title)}
                                                                    >
                                                                        {copiedSocialField === `${draft._id}-title` ? 'Copied!' : 'Copy Title'}
                                                                    </button>
                                                                </div>
                                                            )}

                                                            <textarea
                                                                className="draft-card__textarea"
                                                                value={currentBody}
                                                                onChange={(e) => setEditedSocialBody(prev => ({ ...prev, [draft._id]: e.target.value }))}
                                                                rows={draft.platform === 'reddit_post' ? 6 : 3}
                                                            />
                                                            {draft.charLimit && (
                                                                <p className={`social-draft__char-count ${overLimit ? 'social-draft__char-count--over' : ''}`}>
                                                                    {currentBody.length} / {draft.charLimit} characters
                                                                </p>
                                                            )}

                                                            <div className="draft-card__actions">
                                                                {isEdited && (
                                                                    <button className="btn-sm action-btn response-btn" onClick={() => handleSaveSocialEdit(draft)}>
                                                                        Save edit
                                                                    </button>
                                                                )}
                                                                <button
                                                                    className="btn-sm action-btn response-btn"
                                                                    onClick={() => handleCopySocialText(`${draft._id}-body`, currentBody)}
                                                                >
                                                                    {copiedSocialField === `${draft._id}-body` ? 'Copied!' : (draft.platform === 'reddit_post' ? 'Copy Body' : 'Copy')}
                                                                </button>
                                                                {draft.status === 'pending_review' && (
                                                                    <>
                                                                        <button className="btn-sm action-btn success-btn" onClick={() => handleSocialDraftStatusChange(draft, 'posted')}>
                                                                            Mark posted
                                                                        </button>
                                                                        <button className="btn-sm action-btn danger-btn" onClick={() => handleSocialDraftStatusChange(draft, 'dismissed')}>
                                                                            Dismiss
                                                                        </button>
                                                                    </>
                                                                )}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                )}

            {/* Legacy migration results */}
            {legacyMigrationResults && (
                <div className="migration-results">
                    <h3>Legacy Sponsor Migration Results</h3>
                    <div className="migration-stats">
                        <div className="migration-stat">
                            <strong>Matched:</strong> {legacyMigrationResults.matchedCount} sponsor(s)
                        </div>
                        <div className="migration-stat">
                            <strong>Updated:</strong> {legacyMigrationResults.modifiedCount} sponsor(s)
                        </div>
                    </div>
                    <button
                        className="btn btn-sm btn-secondary"
                        onClick={() => setLegacyMigrationResults(null)}
                    >
                        Dismiss
                    </button>
                </div>
            )}

            {/* Consolidation Results */}
            {consolidationResults && (
                <div className="migration-results">
                    <h3>Data Consolidation Results</h3>
                    <div className="migration-stats">
                        <div className="migration-stat">
                            <strong>Total Consolidated:</strong> {consolidationResults.totalConsolidated} sponsors
                        </div>
                        <div className="migration-stat">
                            <strong>Potential Sponsors:</strong> {consolidationResults.potentialSponsors.consolidated} consolidated
                        </div>
                        <div className="migration-stat">
                            <strong>Approved Sponsors:</strong> {consolidationResults.sponsors.consolidated} consolidated
                        </div>
                        <div className="migration-stat">
                            <strong>Errors:</strong> {consolidationResults.potentialSponsors.errors + consolidationResults.sponsors.errors} errors
                        </div>
                    </div>
                    {consolidationResults.details && consolidationResults.details.length > 0 && (
                        <div style={{ marginTop: '16px' }}>
                            <h4>Consolidation Details:</h4>
                            <div style={{ maxHeight: '200px', overflowY: 'auto', fontSize: '12px' }}>
                                {consolidationResults.details.map((detail: any, index: number) => (
                                    <div key={index} style={{ marginBottom: '4px', padding: '4px', background: '#f0fdf4', borderRadius: '4px' }}>
                                        {detail}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                    <button 
                        className="btn btn-sm btn-secondary"
                        onClick={() => setConsolidationResults(null)}
                    >
                        Dismiss
                    </button>
                </div>
            )}

            {/* Hunter approval toast */}
            {hunterToast && (
                <div className={`admin-toast admin-toast--${hunterToast.type}`}>
                    <span>{hunterToast.message}</span>
                    <button type="button" onClick={() => setHunterToast(null)} aria-label="Dismiss">
                        <FontAwesomeIcon icon={faTimesIcon} />
                    </button>
                </div>
            )}

            {/* Error Display */}
            {error && (
                <div className="error-banner">
                    <FontAwesomeIcon icon={faExclamationTriangle} />
                    <span>{error}</span>
                    <button onClick={() => setError(null)}>
                        <FontAwesomeIcon icon={faTimesIcon} />
                    </button>
                </div>
            )}

            {/* Edit Sponsor Modal */}
            {editingSponsor && (
                <EditSponsorModal
                    sponsor={editingSponsor}
                    onClose={handleCloseEditModal}
                    onSave={handleSaveSponsor}
                    onConvertToAffiliate={handleConvertToAffiliate}
                    onDelete={handleDeleteAndBlacklistSponsor}
                />
            )}
            </div>
        </div>
    );
};

export default Admin;
